import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { portalUrl } from '../config/site';
import { productGroups } from '../config/products';

const HeaderNew = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const handleMouseEnter = (menu) => {
    setActiveDropdown(menu);
  };

  const handleMouseLeave = () => {
    setActiveDropdown(null);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-[#0a1f2e]">
      {/* relative: the products panel is positioned against this container so it stays on screen at any width */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="font-display text-xl font-semibold tracking-tight text-white">
              VOIP<span style={{ color: '#c8ff00' }}>2</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {/* Products Menu */}
            <div
              className="flex h-14 items-center"
              onMouseEnter={() => handleMouseEnter('products')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                type="button"
                aria-expanded={activeDropdown === 'products'}
                onClick={() => setActiveDropdown('products')}
                className="flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium text-white/80 transition-colors hover:text-white"
              >
                <span>Products</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${activeDropdown === 'products' ? 'rotate-180' : ''}`} />
              </button>
              {activeDropdown === 'products' && (
                <div className="absolute left-1/2 top-full w-[min(960px,calc(100%-2rem))] -translate-x-1/2">
                  <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_20px_50px_-12px_rgba(10,31,46,0.35)]">
                    <div className="grid grid-cols-3 gap-2 p-4">
                      {productGroups.map((group) => (
                        <div key={group.id} className="px-2">
                          <p className="px-2 pb-2 pt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">{group.title}</p>
                          {group.items.map((product) => (
                            <Link
                              key={product.to}
                              to={product.to}
                              className="group flex items-center gap-3 rounded-xl p-2 hover:bg-gray-50"
                              onClick={handleMouseLeave}
                            >
                              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0a1f2e] text-[#c8ff00]">
                                <product.icon className="h-5 w-5" strokeWidth={1.75} />
                              </span>
                              <span className="min-w-0">
                                <span className="block text-sm font-semibold text-gray-900 group-hover:text-[#0a1f2e]">{product.title}</span>
                                <span className="block truncate text-xs text-gray-500">{product.subtitle}</span>
                              </span>
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between gap-4 border-t border-gray-100 bg-[#f5f7f9] px-6 py-3 text-sm">
                      <span className="text-gray-600">Numbers in 140 countries, with local, national, mobile and freephone ranges.</span>
                      <Link to="/coverage" onClick={handleMouseLeave} className="inline-flex shrink-0 items-center gap-1.5 font-semibold text-[#0a1f2e] hover:underline">
                        Coverage & pricing <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link to="/coverage" className="rounded-full px-3 py-1.5 text-sm font-medium text-white/80 transition-colors hover:text-white">
              Coverage & Pricing
            </Link>
            <Link to="/regulatory-requirements" className="rounded-full px-3 py-1.5 text-sm font-medium text-white/80 transition-colors hover:text-white">
              Regulations & Requirements
            </Link>
            <Link to="/resources" className="rounded-full px-3 py-1.5 text-sm font-medium text-white/80 transition-colors hover:text-white">
              Resources
            </Link>
            <Link to="/about" className="rounded-full px-3 py-1.5 text-sm font-medium text-white/80 transition-colors hover:text-white">
              About Us
            </Link>
            <Link to="/contact" className="rounded-full px-3 py-1.5 text-sm font-medium text-white/80 transition-colors hover:text-white">
              Contact Us
            </Link>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-2">
            <a
              href={portalUrl('/customer/login')}
              className="rounded-full px-3 py-1.5 text-sm font-medium text-white/80 transition-colors hover:text-white"
            >
              Sign in
            </a>
            <Link
              to="/contact"
              className="rounded-full bg-[#c8ff00] px-4 py-1.5 text-sm font-semibold text-[#0a1f2e] transition hover:brightness-95 active:scale-[0.98]"
            >
              Get started
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#0a1f2e] border-t border-gray-700 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-4 space-y-3">
            <div className="text-white font-semibold mb-2">Products</div>
            {productGroups.map((group) => (
              <div key={group.id} className="pb-2">
                <p className="pl-4 pt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#c8ff00]/80">{group.title}</p>
                {group.items.map((product) => (
                  <Link
                    key={product.to}
                    to={product.to}
                    className="block py-2 pl-4 text-gray-300 hover:text-[#c8ff00]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {product.title}
                  </Link>
                ))}
              </div>
            ))}

            <div className="border-t border-gray-700 pt-3 mt-3 space-y-1">
              <Link to="/coverage" className="block py-2 text-white font-semibold hover:text-[#c8ff00]" onClick={() => setMobileMenuOpen(false)}>Coverage & Pricing</Link>
              <Link to="/regulatory-requirements" className="block py-2 text-white font-semibold hover:text-[#c8ff00]" onClick={() => setMobileMenuOpen(false)}>Regulations & Requirements</Link>
              <Link to="/resources" className="block py-2 text-white font-semibold hover:text-[#c8ff00]" onClick={() => setMobileMenuOpen(false)}>Resources</Link>
              <Link to="/about" className="block py-2 text-white font-semibold hover:text-[#c8ff00]" onClick={() => setMobileMenuOpen(false)}>About Us</Link>
              <Link to="/contact" className="block py-2 text-white font-semibold hover:text-[#c8ff00]" onClick={() => setMobileMenuOpen(false)}>Contact Us</Link>
            </div>

            <div className="pt-4 space-y-2 pb-4">
              <Button variant="outline" className="w-full border-[#c8ff00] text-[#c8ff00]" onClick={() => { setMobileMenuOpen(false); window.location.href = portalUrl('/customer/login'); }}>
                Sign in
              </Button>
              <Button style={{ backgroundColor: '#c8ff00', color: '#0a1f2e' }} className="w-full" onClick={() => { setMobileMenuOpen(false); window.location.href = '/contact'; }}>
                Get Started
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default HeaderNew;
